<template>
  <div>
    <pv-exams-import-report-alert
      :import-info-id="importInfoIdForReportAlert"
      :is-partial-success="true"
    />

    <v-container>
      <pv-hero
        ref="hero"
        :title="$t('page.importedExamsList.title')"
      />
    </v-container>
    <v-main>
      <v-container
        class="table-width"
      >
        <v-data-table
          class="imported-exams-table"
          :headers="headers"
          :items="exams"
          item-key="lineNumber"
          :options="options"
          disable-filtering
          disable-pagination
          disable-sort
          hide-default-footer
          :loading="isLoading"
        >
          <template #item.trafficLight="{ item }">
            <div class="table-cell">
              <pv-traffic-light :color="getTrafficLightColor(item)" />
            </div>
          </template>
          <template #item.lineNumber="{ item }">
            <div
              class="table-cell id justify-end"
              :class="classes(item)"
            >
              {{ item.lineNumber }}
            </div>
          </template>
          <template #item.examinationCenter="{ item }">
            <div
              class="table-cell"
              :class="classes(item)"
            >
              <span class="table-cell-inner flex-column align-start examination-center">
                <span v-if="item.bpName">
                  {{ item.bpName }}
                </span>
                <span>
                  {{ item.extRef }}
                </span>
                <div v-if="item.contactPerson">
                  <div class="text-body-2 font-weight-bold mt-4">
                    {{ $t('page.importedExamsList.headers.contactPerson') }}
                  </div>
                  <span>
                    {{ getContactAsShortString(item.contactPerson) }}
                  </span>
                </div>
              </span>
            </div>
          </template>
          <template #item.subject="{ item }">
            <div
              class="table-cell"
              :class="classes(item)"
            >
              <span class="table-cell-inner flex-column align-start">
                <span v-if="item.subjectTitle">
                  {{ item.subjectTitle }}
                </span>
                <span v-if="item.subjectId">
                  {{ item.subjectId }}
                </span>
              </span>
            </div>
          </template>
          <template #item.examGroups="{ item }">
            <div
              class="table-cell"
              :class="classes(item)"
            >
              <v-container>
                <span class="text-body-2 font-weight-bold">
                  {{ $t('page.importedExamsList.headers.totalNumberAttendees') }}:
                </span>
                <span>
                  {{ item.totalNumberAttendees }}
                </span>
                <div class="text-body-2 font-weight-bold">
                  {{ $t('page.importedExamsList.headers.writingExamGroups') }}
                </div>
                <ul>
                  <li
                    v-for="(group, index) in item.writingExamGroups"
                    :key="index"
                  >
                    {{ getGroupInfo(group, index) }}
                  </li>
                </ul>
                <div class="text-body-2 font-weight-bold mt-4">
                  {{ $t('page.importedExamsList.headers.oralExamGroups') }}
                </div>
                <ul>
                  <li
                    v-for="(group, index) in item.oralExamGroups"
                    :key="index"
                  >
                    {{ getGroupInfo(group, index) }}
                  </li>
                </ul>
              </v-container>
            </div>
          </template>
          <template #item.oralExamGroups="{ item }">
            <div
              class="table-cell"
              :class="classes(item)"
            >
              <ul>
                <li
                  v-for="(group, index) in item.oralExamGroups"
                  :key="index"
                >
                  {{ group }}
                </li>
              </ul>
            </div>
          </template>
          <template #item.addresses="{ item }">
            <div
              class="table-cell"
              :class="classes(item)"
            >
              <span class="table-cell-inner flex-column align-start addresses">
                <v-autocomplete
                  v-model="item.invoiceAddress"
                  :items="item.invoiceAddressItems"
                  :item-text="getAddressAsShortString"
                  :label="$t('page.importedExamsList.headers.invoiceAddress')"
                  return-object
                  :no-data-text="ui.errors.noDataAvailable"
                  hide-selected
                  required
                  autocomplete="off"
                  type="search"
                  :rules="isEmptyRule"
                  :disabled="isLoading"
                />
                <v-autocomplete
                  v-model="item.deliveryAddress"
                  :items="item.deliveryAddressItems"
                  :item-text="getAddressAsShortString"
                  :label="$t('page.importedExamsList.headers.deliveryAddress')"
                  return-object
                  :no-data-text="ui.errors.noDataAvailable"
                  hide-selected
                  required
                  autocomplete="off"
                  type="search"
                  :rules="isEmptyRule"
                  :disabled="isLoading"
                />
                <v-autocomplete
                  v-model="item.deliveryCertificatesAddress"
                  :items="item.deliveryCertificatesAddressItems"
                  :item-text="getAddressAsShortString"
                  :label="$t('page.importedExamsList.headers.deliveryCertificatesAddress')"
                  return-object
                  :no-data-text="ui.errors.noDataAvailable"
                  hide-selected
                  required
                  autocomplete="off"
                  type="search"
                  :rules="isEmptyRule"
                  :disabled="isLoading"
                />
                <v-autocomplete
                  v-model="item.examinationLocationAddress"
                  :items="item.examinationLocationAddressItems"
                  :item-text="getAddressAsShortStringForExaminationLocation"
                  :label="$t('page.importedExamsList.headers.examinationLocation')"
                  return-object
                  :no-data-text="ui.errors.noDataAvailable"
                  hide-selected
                  required
                  autocomplete="off"
                  type="search"
                  :rules="isEmptyRule"
                  :disabled="isLoading || isDeviatingLocation(item)"
                />
                {{ item.alternativeExaminationLocationText }}
              </span>
            </div>
          </template>
          <template #item.notes="{ item }">
            <div
              class="table-cell"
              :class="classes(item)"
            >
              <v-container>
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <div
                      class="text-body-2 font-weight-bold mt-4"
                      v-bind="attrs"
                      v-on="on"
                    >
                      {{ $t('page.importedExamsList.headers.additionalNotes') }}
                    </div>
                  </template>
                  <span>{{ $t('page.importedExamsList.headers.additionalNotesHint') }}</span>
                </v-tooltip>
                {{ item.additionalNotes }}

                <div
                  v-if="getValidationErrorMessages(item).length"
                  class="text-body-2 font-weight-bold mt-4 error--text"
                >
                  {{ $t('page.importedExamsList.headers.validationErrors') }}
                </div>
                <ul
                  v-if="getValidationErrorMessages(item).length"
                  class="text-body-2 import-validation-errors"
                >
                  <li
                    v-for="(message, index) in getValidationErrorMessages(item)"
                    :key="index"
                  >
                    {{ message }}
                  </li>
                </ul>
              </v-container>
            </div>
          </template>
          <template #item.actions="{ item }">
            <div
              class="table-cell actions"
              :class="classes(item)"
            >
              <span class="table-cell-inner single-button">
                <v-tooltip bottom>
                  <template #activator="{ on, attrs }">
                    <a
                      v-bind="attrs"
                      v-on="on"
                      @click="onDelete(item)"
                    >
                      <v-icon
                        color="primary"
                        small
                      >
                        $vuetify.icons.deleteAction
                      </v-icon>
                    </a>
                  </template>
                  <span>{{ $t('page.importedExamsList.actions.deleteSingleExam') }}</span>
                </v-tooltip>
              </span>
            </div>
          </template>
        </v-data-table>

        <v-container
          v-if="hasExam"
          class="checkboxes"
        >
          <v-row
            align="center"
            class="flex-nowrap"
          >
            <v-col cols="auto">
              <v-checkbox
                v-model="termsAndConditions.telcLicence.accepted"
                :rules="isEmptyRuleApproval"
              />
            </v-col>
            <v-col>
              <p class="mb-0">
                {{ $t('page.importedExamsList.fields.agreementExaminerLicence') }}
              </p>
            </v-col>
          </v-row>
          <v-row
            align="center"
            class="flex-nowrap"
          >
            <v-col cols="auto">
              <v-checkbox
                v-model="termsAndConditions.bamf.accepted"
                :rules="isEmptyRuleApproval"
              />
            </v-col>
            <v-col>
              <p class="mb-0">
                {{ $t('page.importedExamsList.fields.agreementInstitutionLicence') }}
              </p>
            </v-col>
          </v-row>
          <v-row
            align="center"
            class="flex-nowrap"
          >
            <v-col cols="auto">
              <v-checkbox
                v-model="termsAndConditions.telcAgb.accepted"
                :rules="isEmptyRuleApproval"
              />
            </v-col>
            <v-col>
              <p class="mb-0">
                {{ $t('page.importedExamsList.fields.agreementTerms&Conditions') }}
              </p>
            </v-col>
          </v-row>
          <v-row
            v-if="hasAlternativeExaminationLocation"
            align="center"
            class="flex-nowrap"
          >
            <v-col cols="auto">
              <v-checkbox
                v-model="termsAndConditions.agbDifferentExamPlace.accepted"
                class="agbDifferentExamPlace"
                :rules="isEmptyRuleApproval"
              />
            </v-col>
            <v-col>
              <p
                class="mb-0"
                v-html="$t('page.importedExamsList.fields.agbDifferentExamPlace')"
              />
            </v-col>
          </v-row>
          <v-row
            v-if="hasDtz"
            align="center"
            class="flex-nowrap"
          >
            <v-col cols="auto">
              <v-checkbox
                v-model="termsAndConditions.dtz.accepted"
                class="dtz"
                :rules="isEmptyRuleApprovalDtz"
              />
            </v-col>
            <v-col>
              <p class="mb-0">
                {{ $t('page.importedExamsList.fields.agreementDtz') }}
              </p>
            </v-col>
          </v-row>
        </v-container>

        <v-container>
          <pv-actionbar
            :primary-actions="ui.primary"
            :secondary-actions="ui.secondary"
            @import="onImport"
            @abort="onAbort"
          />
        </v-container>
      </v-container>
    </v-main>
  </div>
</template>

<script lang="js">
import _ from "lodash";
import {v4 as uuidV4} from 'uuid';
import {mapActions, mapMutations, mapState} from "vuex";

import pvHero from "@/components/Hero.vue";
import pvTrafficLight from "@/components/TrafficLight.vue"
import pvActionbar from "@/components/Actionbar.vue";
import {getCountries} from "@/data";

import pvExamsImportReportAlert from "@/components/ExamsImportReportAlert.vue";

const deviatingLocation = "9999"

export default {
  name: 'ImportedExamsList',

  components: {
    pvExamsImportReportAlert,
    pvHero,
    pvTrafficLight,
    pvActionbar
  },

  inject: [
    'institutverwaltung',
    'pruefungsfachverwaltung',
    'pruefungsverwaltung'
  ],

  props: {
    initialExams: {
      type: Array,
      default: () => []
    }
  },

  data() {
    const countries = getCountries().reduce(
      (countriesMap, country) => ({...countriesMap, [country.code]: country.label}),
      {}
    )
    return {
      countries,
      exams: this.createExams(this.initialExams, countries),

      isLoading: true,

      termsAndConditions: {
        telcLicence: {
          accepted: false
        },
        bamf: {
          accepted: false
        },
        telcAgb: {
          accepted: false
        },
        agbDifferentExamPlace: {
          accepted: false
        },
        dtz: {
          accepted: false
        }
      },

      importInfoIdForReportAlert: undefined,

      isEmptyRule: [
        (v) => !!v || v !== ' ' || vm.$t('page.importedExamsList.messages.requiredField')
      ],
      isEmptyRuleApproval: [
        (v) => !!v || this.$t('page.importedExamsList.messages.requiredField')
      ],
      isEmptyRuleApprovalDtz: [
        (v) => !this.isDtz || !!v || this.$t('page.importedExamsList.messages.requiredField')
      ]
    }
  },

  computed: {
    ...mapState({
      eiByExtRef: state => state.institutes.eiByExtRef,
      ecByExtRef: state => state.institutes.ecByExtRef,
      subjectById: state => state.subjects.subjectById
    }),

    headers() {
      return [
        {
          value: 'trafficLight'
        },
        {
          text: this.$t('page.importedExamsList.headers.lineNumber'),
          value: 'lineNumber'
        },
        {
          text: this.$t('page.importedExamsList.headers.examinationCenter'),
          value: 'examinationCenter'
        },
        {
          text: this.$t('page.importedExamsList.headers.subject'),
          value: 'subject'
        },
        {
          text: this.$t('page.importedExamsList.headers.examGroups'),
          value: 'examGroups'
        },
        {
          text: this.$t('page.importedExamsList.headers.addresses'),
          value: 'addresses'
        },
        {
          text: this.$t('page.importedExamsList.headers.notes'),
          value: 'notes'
        },
        {
          text: this.$t('page.importedExamsList.headers.actions'),
          value: 'actions'
        }
      ]
    },

    options() {
      return {
        sortBy: ['lineNumber'],
        sortDesc: [false]
      }
    },
    ui() {
      return {
        primary: [
          {
            title: this.$t('page.importedExamsList.actions.import'),
            color: 'primary',
            icon: '$vuetify.icons.importAction',
            event: 'import',
            disabled: !this.enableImportButton
          }
        ],
        secondary: [
          {
            title: this.$t('page.importedExamsList.actions.abort'),
            outlined: true,
            color: 'white',
            event: 'abort'
          }
        ],
        errors: {
          noDataAvailable: this.$t('page.importedExamsList.messages.noDataAvailable')
        }
      }
    },

    enableImportButton() {
      if (this.isLoading) {
        return false
      }

      if (this.exams.length === 0) {
        return false
      }

      const allGeneralCheckboxesChecked =
        this.termsAndConditions.telcLicence.accepted &&
        this.termsAndConditions.bamf.accepted &&
        this.termsAndConditions.telcAgb.accepted
      const allRequiredCheckboxesChecked =
        allGeneralCheckboxesChecked &&
        (!this.hasDtz || this.termsAndConditions.dtz.accepted) &&
        (!this.hasAlternativeExaminationLocation || this.termsAndConditions.agbDifferentExamPlace.accepted)

      if (!allRequiredCheckboxesChecked) {
        return false
      }

      for (const exam of this.exams) {
        if (exam.fatalValidationErrors.length > 0) {
          return false
        }
        if (this.hasNonFatalValidationErrors(exam)) {
          return false
        }
      }

      return true
    },

    hasExam() {
      return this.exams.length > 0
    },
    hasDtz() {
      return !!this.exams.find(exam => this.isDtz(exam.subjectId))
    },
    hasAlternativeExaminationLocation() {
      return !!this.exams.find(exam => exam.examinationLocation?.isAlternative === true)
    }
  },

  async created() {
    try {
      await Promise.all([
        this.pullInstitutes(),
        this.pullSubjects()
      ])

      const singleOrDefault = (list, defaultValue = {}) =>
        list?.length === 1 ? list[0] : defaultValue

      const providedOrDefault = (list, provided, defaultValue = {}) => {
        if (!provided) {
          return defaultValue
        }

        if (list.length > 0 && list[0].text && !list[0].country) {
          return list.length === 1 ? list[0] : defaultValue
        }

        const countryAndZipMatches = list.filter(item =>
          item.country === provided.country &&
          item.zipCode === provided.zipCode)

        if (countryAndZipMatches.length === 1) {
          return countryAndZipMatches[0]
        }

        if (countryAndZipMatches.length > 1) {
          const exactMatches = countryAndZipMatches.filter(item =>
            item.street === provided.street &&
            item.houseId === provided.houseId &&
            item.city === provided.city)

          if (exactMatches.length === 1) {
            return exactMatches[0]
          }
        }

        return defaultValue
      }

      this.exams.forEach((exam, index) => {
        const businessPartner = this.ecByExtRef[exam.extRef] ?? this.eiByExtRef[exam.extRef]
        this.exams[index].bpName = businessPartner?.name

        const contactPersonItems = this.contactPersonItems(exam.extRef)
          .sort((a, b) => this.compare(this.getContactAsShortString(a), this.getContactAsShortString(b)))
        this.exams[index].contactPerson = this.getContactByEmailOrDefault(contactPersonItems, exam.contactEmail)

        const subjectTitle = this.subjectById[exam.subjectId]?.title
        this.exams[index].subjectTitle = subjectTitle

        const invoiceAddressItems = this.invoiceAddressItems(exam.extRef, exam.subjectId)
        const providedInvoiceAddress = this.exams[index].providedAddresses.invoiceAddress
        this.exams[index].invoiceAddressItems = invoiceAddressItems
        this.exams[index].invoiceAddress = providedOrDefault(invoiceAddressItems, providedInvoiceAddress)

        const deliveryAddressItems = this.deliveryAddressItems(exam.extRef)
        const providedDeliveryAddress = this.exams[index].providedAddresses.deliveryAddress
        this.exams[index].deliveryAddressItems = deliveryAddressItems
        this.exams[index].deliveryAddress = providedOrDefault(deliveryAddressItems, providedDeliveryAddress)

        const deliveryCertificatesAddressItems = this.deliveryAddressItems(exam.extRef)
        const providedDeliveryCertificatesAddress = this.exams[index].providedAddresses.deliveryCertificatesAddress
        this.exams[index].deliveryCertificatesAddressItems = deliveryCertificatesAddressItems
        this.exams[index].deliveryCertificatesAddress = providedOrDefault(deliveryCertificatesAddressItems, providedDeliveryCertificatesAddress)

        const isAlternativeLocation = exam.examinationLocation?.isAlternative === true
        const examinationLocationAddressItems = this.examinationLocationAddressItems(exam.extRef, exam.subjectId, isAlternativeLocation)
        this.exams[index].examinationLocationAddressItems = examinationLocationAddressItems
        if (isAlternativeLocation && exam.examinationLocation) {
          this.exams[index].examinationLocationAddress = providedOrDefault(examinationLocationAddressItems, exam.examinationLocation)
        } else {
          this.exams[index].examinationLocationAddress = singleOrDefault(examinationLocationAddressItems)
        }

        this.exams[index].fatalValidationErrors = this.fatalValidationErrors(this.exams[index])
      })
    } finally {
      this.isLoading = false
    }
  },

  methods: {
    ...mapActions('institutes', {
      refreshInstitutes: 'refresh'
    }),
    ...mapActions('subjects', {
      refreshSubjects: 'refresh'
    }),
    ...mapMutations('notice', {
      addNotice: 'add'
    }),

    async pullInstitutes() {
      const response = await this.institutverwaltung.getInstitutes()
      await this.refreshInstitutes(response.data.institutes)
    },
    async pullSubjects() {
      const response = await this.pruefungsfachverwaltung.getCourses()
      await this.refreshSubjects(response.data.examSubjects)
    },

    async onImport() {
      const importInfoId = uuidV4()

      const termsAndConditions = {
        telcLicence: {
          accepted: this.termsAndConditions.telcLicence.accepted
        },
        bamf: {
          accepted: this.termsAndConditions.bamf.accepted
        },
        telcAgb: {
          accepted: this.termsAndConditions.telcAgb.accepted
        },
        agbDifferentExamPlace: {
          accepted: this.termsAndConditions.agbDifferentExamPlace.accepted
        }
      }
      if (this.termsAndConditions.dtz.accepted) {
        termsAndConditions.dtz = {accepted: true}
      }

      const createExamSubject = (examSubject) => ({
        id: examSubject.id,
        level: examSubject.level,
        language: examSubject.language
      })

      const createExaminationAuthority = (bp) => ({
        uuid: bp.uuid,
        extRef: bp.extRef,
        name: bp.name
      })

      const createContact = (contactPerson) => ({
        uuid: contactPerson.uuid,
        title: contactPerson.title,
        firstName: contactPerson.firstName,
        lastName: contactPerson.lastName,
        email: contactPerson.email,
        phone: contactPerson.phone
      })

      const createAddress = (address) => ({
        uuid: address.uuid,
        ownerUuid: address.ownerUuid,
        extRef: address.extRef,
        name: address.name,
        street: address.street,
        houseId: address.houseId,
        zipCode: address.zipCode,
        city: address.city,
        country: address.country,
        phone: address.phone,
        email: address.email,
        sto: address.sto
      })

      const createExaminationLocation = (address, examinationLocation) => {
        if (examinationLocation?.isAlternative === true) {
          return {
            extRef: deviatingLocation,
            street: examinationLocation.street,
            houseId: examinationLocation.houseId,
            zipCode: examinationLocation.zipCode,
            city: examinationLocation.city,
            country: examinationLocation.country,
            sto: examinationLocation.sto
          }
        }
        return createAddress(address)
      }

      const createExamGroup = (group) => ({
        examDate: group.examDate,
        groupSize: group.groupSize
      })

      let hasFailedImport = false
      let hasSuccessfulImport = false

      for (const exam of this.exams) {
        const examSubject = this.subjectById[exam.subjectId]
        if (!examSubject) {
          console.warn('examSubject not found', exam.subjectId, this.subjectById)
          continue
        }
        const ei = this.eiByExtRef[exam.extRef]
        if (!ei) {
          console.warn('examinationInstitute not found', exam.extRef)
          continue
        }
        const ec = this.ecByExtRef[exam.extRef] || ei
        if (!ec) {
          console.warn('examinationCenter not found', exam.extRef)
          continue
        }

        const examDate = exam.writingExamGroups?.length > 0
          ? exam.writingExamGroups[0].examDate
          : undefined

        const importInfo = {
          id: importInfoId,
          timezone: exam.timezone,
          lineNumber: exam.lineNumber
        }

        const createExamEntity = {
          status: 'in_submission',
          examDate,
          examSubject: createExamSubject(examSubject),
          examinationInstitute: createExaminationAuthority(ei),
          examinationCenter: createExaminationAuthority(ec),
          contactPerson: createContact(exam.contactPerson),
          invoiceAddress: createAddress(exam.invoiceAddress),
          deliveryAddress: createAddress(exam.deliveryAddress),
          deliveryCertificatesAddress: createAddress(exam.deliveryCertificatesAddress),
          examinationLocation: createExaminationLocation(exam.examinationLocationAddress, exam.examinationLocation),
          oralExamGroups: exam.oralExamGroups.map((group) => createExamGroup(group)),
          writingExamGroups: exam.writingExamGroups.map((group) => createExamGroup(group)),
          additionalNotes: exam.additionalNotes,
          internalNotes: exam.internalNotes,
          termsAndConditions,
          totalNumberAttendees: exam.totalNumberAttendees,
          importInfo
        }

        try {
          await this.pruefungsverwaltung.createExam(ei.uuid, createExamEntity)

          hasSuccessfulImport = true
          this.onDelete(exam)
        } catch (e) {
          hasFailedImport = true
          console.warn('failed to transmit', e)
          this.addNotice({
            message: this.$t('page.importedExamsList.messages.failedToTransmit', {lineNumber: exam.lineNumber}),
            type: 'error'
          })
        }
      }

      if (!hasFailedImport) {
        await this.$router.push({
          name: 'list',
          params: {
            importInfoId
          }
        })
      } else if (hasSuccessfulImport) {
        this.importInfoIdForReportAlert = importInfoId
      }
    },
    onAbort() {
      this.$router.push({name: 'list'});
    },
    onDelete(itemToDelete) {
      this.exams = this.exams.filter((item) => item.lineNumber !== itemToDelete.lineNumber)
    },

    fatalValidationErrors(item) {
      let errors = []

      // Check institute and business partner
      if (!item.extRef) {
        errors.push({
          error: 'missingExtRef'
        })
      } else if (!item.bpName) {
        errors.push({
          error: 'missingOrInvalidBusinessPartner'
        })
      } else {
        const ei = this.eiByExtRef[item.extRef]
        if (!ei) {
          errors.push({
            error: 'missingExaminationInstitute'
          })
        }

        if (!item.contactPerson) {
          errors.push({
            error: 'missingOrInvalidContactPerson'
          })
        }
      }

      // check exam subject
      if (!item.subjectTitle) {
        errors.push({
          error: 'missingOrInvalidSubject'
        })
      }

      // check total number of attendees
      if (!item.totalNumberAttendees) {
        errors.push({
          error: 'missingTotalNumberAttendees'
        })
      }

      // check groups
      for (const group of item.writingExamGroups) {
        if (group.errors) {
          errors.push({
            error: 'writingExamGroupErrors',
            details: group.errors
          })
        }
      }
      for (const group of item.oralExamGroups) {
        if (group.errors) {
          errors.push({
            error: 'oralExamGroupErrors',
            details: group.errors
          })
        }
      }

      // check the alternative examination location
      if (item.examinationLocation) {
        const examLoc = item.examinationLocation
        if (examLoc.isAlternative === true) {
          if (!examLoc.street || !examLoc.houseId || !examLoc.zipCode || !examLoc.city || !examLoc.country) {
            errors.push({
              error: 'missingAlternativeExaminationLocationField'
            })
          }
          if (examLoc.country && !this.countries[examLoc.country]) {
            errors.push({
              error: 'invalidAlternativeExaminationLocationCountry'
            })
          }
        }
        if (examLoc.errors) {
          errors.push({
            error: 'alternativeExaminationLocationErrors',
            details: examLoc.errors
          })
        }
      }

      // any other error
      if (item.errors) {
        errors.push({
          error: 'generalErrors',
          details: item.errors
        })
      }

      return errors
    },
    hasNonFatalValidationErrors(item) {
      const isAlternativeLocation = item.examinationLocation?.isAlternative === true

      return _.isEmpty(item.invoiceAddress)
        || _.isEmpty(item.deliveryAddress)
        || _.isEmpty(item.deliveryCertificatesAddress)
        || (!isAlternativeLocation && _.isEmpty(item.examinationLocationAddress))
    },
    getTrafficLightColor(item) {
      if (!item || !item.fatalValidationErrors) {
        return ''
      }

      if (item.fatalValidationErrors.length > 0) {
        return 'red'
      }

      if (this.hasNonFatalValidationErrors(item)) {
        return 'orange'
      }

      return 'green'
    },
    classes(item) {
      const trafficLightColor = this.getTrafficLightColor(item)
      return {
        'has-validation-error': trafficLightColor === 'orange',
        'has-fatal-validation-error': trafficLightColor === 'red'
      }
    },

    getContactAsShortString(contact) {
      if (!contact || (!contact.firstName && !contact.lastName)) {
        return ''
      }
      return `${contact.lastName}, ${contact.firstName}`
    },
    getValidationErrorMessages(item) {
      return (item.fatalValidationErrors ?? [])
        .flatMap((validationError) => this.getValidationErrorMessagesForError(validationError))
        .filter(Boolean)
    },
    getValidationErrorMessagesForError(validationError) {
      if (!validationError.details?.length) {
        return [this.getValidationErrorMessage(validationError)]
      }

      return validationError.details.map((detail) => this.getImportErrorMessage(detail))
    },
    getValidationErrorMessage(validationError) {
      return this.$t(`page.importedExamsList.validationErrors.${validationError.error}`)
    },
    getImportErrorMessage(error) {
      if (error.message) {
        return error.message
      }

      return this.$t('page.importedExamsList.validationErrors.importError', {
        field: error.field,
        type: error.type,
        value: error.value
      })
    },
    getGroupInfo(group, idx) {
      return this.$t('page.importedExamsList.fields.groups.label', {
        date: this.formatDate(group.examDate, 'DD.MM.YYYY HH:mm') ?? this.$t('page.importedExamsList.fields.groups.missingDate'),
        number: idx + 1,
        size: group.groupSize ?? this.$t('page.importedExamsList.fields.groups.missingSize')
      })
    },
    getAddressAsString(countries, address) {
      const country = address.country ? `, ${countries[address.country]}` : '';
      const stoAsString = address.sto ? `STO:${address.sto}, ` : ''
      return stoAsString + this.getAddressAsShortString(address) + country
    },
    getAddressAsShortString(address) {
      return `${address.street} ${address.houseId}, ${address.zipCode} ${address.city}`
    },
    getAddressAsShortStringForExaminationLocation(address) {
      return address.text ?? this.getAddressAsShortString(address)
    },

    compare(a, b) {
      return a < b ? -1 : (a > b ? 1 : 0)
    },

    contactPersonItems(extRef) {
      if (!extRef) {
        return []
      }

      const normalizeEmail = (email) => email?.trim().toLowerCase() ?? ''

      const contacts = [
        ...(this.ecByExtRef[extRef]?.contacts ?? []),
        ...(this.eiByExtRef[extRef]?.contacts ?? [])
      ]

      return _.uniqBy(contacts, contact => normalizeEmail(contact.email))
    },
    getContactByEmailOrDefault(contacts, contactEmail, defaultValue = undefined) {
      if (!contactEmail || !contacts || !contacts.length) {
        return defaultValue
      }

      // See: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/Collator/Collator#options
      const opts = {usage: 'search', sensitivity: 'accent'}
      const matchesContactEmailPredicate = (email) => contactEmail.localeCompare(email, undefined, opts) == 0

      return contacts.find((c) => matchesContactEmailPredicate(c.email)) ?? defaultValue
    },

    invoiceAddressItems(extRef, subjectId) {
      if (!extRef || !subjectId) {
        return []
      }
      const ei = this.eiByExtRef[extRef]
      const bp = this.ecByExtRef[extRef] ?? ei
      if (!ei || !bp) {
        return []
      }

      const isExceptionalInvoice = ei.exceptionalInvoice.includes(subjectId)
      return this.getSortedActiveAddressesOfType(isExceptionalInvoice ? ei : bp, "BILL_TO")
    },
    deliveryAddressItems(extRef) {
      if (!extRef) {
        return []
      }
      const bp = this.ecByExtRef[extRef] ?? this.eiByExtRef[extRef]
      if (!bp) {
        return []
      }

      return this.getSortedActiveAddressesOfType(bp, "SHIP_TO")
    },
    examinationLocationAddressItems(extRef, subjectId, hasAlternativeExaminationLocation) {
      if (!subjectId) {
        return []
      }

      // TODO what does that even mean?
      if (this.isDigital(subjectId)) {
        return [{
          text: this.$t('page.create.fields.addresses.fields.digitalExamLocation'),
          extRef: deviatingLocation
        }]
      }

      if (hasAlternativeExaminationLocation) {
        return [{
          text: this.$t('page.create.fields.addresses.fields.differentExamLocation'),
          extRef: deviatingLocation
        }]
      }

      if (!extRef) {
        return []
      }

      const bp = this.ecByExtRef[extRef] ?? this.eiByExtRef[extRef]
      if (!bp) {
        return []
      }

      const addressType = this.isDtz(subjectId) ? "DTZ_LOC" : "EXAM_LOC"
      return this.getSortedActiveAddressesOfType(bp, addressType)
    },
    getSortedActiveAddressesOfType(bp, type) {
      return (bp?.addresses ?? [])
        .filter(address => address.addressType === type && address.isActive)
        .map((address) => ({...address, name: bp.name, ownerUuid: bp.uuid}))
        .sort((a, b) => this.compare(this.getAddressAsShortString(a), this.getAddressAsShortString(b)))
    },

    isDtz(subjectId) {
      return subjectId === 1010
    },
    isDigital(subjectId) {
      const subject = this.subjectById[subjectId]
      if (!subject) {
        return undefined
      }
      return subject.format === 'digital'
    },

    isDeviatingLocation(item) {
      return item.examinationLocation?.isAlternative === true ||
        item.examinationLocationAddress?.extRef === deviatingLocation
    },

    createExams(initialExams, countries) {
      return initialExams.map((exam) => {
        const deepCopiedExam = JSON.parse(JSON.stringify(exam))
        const isAlternativeLocation = exam.examinationLocation?.isAlternative === true
        const alternativeExaminationLocationText = isAlternativeLocation ? this.getAddressAsString(countries, exam.examinationLocation) : ''

        return {
          // deep copy of the provided exam
          ...deepCopiedExam,

          bpName: undefined,
          contactPerson: undefined,
          subjectTitle: undefined,

          // provided addresses
          providedAddresses: {
            invoiceAddress: deepCopiedExam.invoiceAddress,
            deliveryAddress: deepCopiedExam.deliveryAddress,
            deliveryCertificatesAddress: deepCopiedExam.deliveryCertificatesAddress,
            examinationLocation: isAlternativeLocation ? deepCopiedExam.examinationLocation : undefined
          },
          // addresses
          invoiceAddressItems: [],
          invoiceAddress: {},
          deliveryAddressItems: [],
          deliveryAddress: {},
          deliveryCertificatesAddressItems: [],
          deliveryCertificatesAddress: {},
          examinationLocationAddressItems: [],
          examinationLocationAddress: {},

          alternativeExaminationLocationText,

          fatalValidationErrors: undefined
        }
      })
    }
  }
}
</script>

<style lang="scss">
.table-width {
  max-width: 100%;

  @media (min-width: 600px) {
    max-width: 600px;
  }

  @media (min-width: 960px) {
    max-width: 960px;
  }

  @media (min-width: 1264px) {
    max-width: 1800px;
  }

  @media (min-width: 1904px) {
    max-width: 1800px;
  }
}

.v-data-table.imported-exams-table tbody tr {
  &:not(.v-data-table__mobile-table-row) {
    td {
      height: 330px !important;

      .table-cell {
        height: 100%;
      }
    }
  }
}

.v-input {
  min-width: 100%;
}

.import-validation-errors {
  padding-left: 1.25rem;
}
</style>
